/**
 * ChatInterview — Main orchestrator for the async chat interview mode.
 *
 * FEAT-11: Now renders a two-panel layout:
 * - Left (70%): Live Process Canvas with real-time entity extraction
 * - Right (30%): Chat window (all existing functionality preserved)
 *
 * Responsive:
 * - Desktop (>=1024px): Two columns side by side
 * - Tablet (768-1023px): Canvas behind toggle, chat full width
 * - Mobile (<768px): Chat only, no canvas
 *
 * Handles:
 * - Loading conversation history on mount (page refresh recovery)
 * - Sending messages through the chat API
 * - TTS for interviewer messages
 * - STT for interviewee voice input
 * - Progress tracking
 * - Interview completion flow
 * - Canvas delta application from chat message responses
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatMessage, InterviewSession } from '@shared/types.ts';
import { cn } from '@/lib/utils';
import { useTTS } from '@/hooks/useTTS';
import { useSTT } from '@/hooks/useSTT';
import { useProcessCanvas } from '@/hooks/useProcessCanvas';
import { useCanvasInterviewBridge, type BridgeCommandHandlers } from '@/hooks/useCanvasInterviewBridge';
import type { AgentFocusRequest } from '@/hooks/useCanvasNavigation';
import type { ClusterProposal } from '@/hooks/useClusterManager';
import {
  getChatHistory,
  sendChatMessage,
  sendNodeContextMessage,
  type NodeContextKind,
  endChatInterview,
  triggerExtraction,
} from '@/services/api';
import { Volume2, VolumeX } from 'lucide-react';
import { ProcessCanvas } from '@/components/canvas/ProcessCanvas';
import { SessionTopbar } from '@/components/interview/SessionTopbar';
import { KaivaAvatar, KaivaWordmark } from '@/components/brand';
import { ChatMessageBubble, type ChatBubbleNode } from './ChatMessageBubble';
import { chatMessageToBubble } from './bubbleTypes';
import { TimelineScrubber, type TimelineEvent } from '@/components/canvas/TimelineScrubber';
import { ChatInput } from './ChatInput';
import { ChatProgress } from './ChatProgress';
import { DevInsightsModal } from './DevInsightsModal';
import { Skeleton, ErrorPill } from '@/components/feedback';

interface ChatInterviewProps {
  session: InterviewSession;
  /** Called when the interview completes (to update parent state) */
  onComplete?: () => void;
}

/** Map language code to full BCP 47 for Web Speech APIs */
function toBcp47(lang: string): string {
  const map: Record<string, string> = {
    de: 'de-DE',
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    pt: 'pt-PT',
  };
  return map[lang] ?? lang;
}

export function ChatInterview({ session, onComplete }: ChatInterviewProps) {
  const navigate = useNavigate();
  const bcp47Lang = toBcp47(session.language ?? 'de');

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSection, setCurrentSection] = useState<string | undefined>();
  const [progressPercent, setProgressPercent] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showCanvas, setShowCanvas] = useState(false); // For tablet toggle
  const [focusMode, setFocusMode] = useState(false); // Phase 5.2 wires the behavior
  const [showLabels, setShowLabels] = useState(false); // Phase 4.4 wires the behavior
  // Phase 6.3: timeline scrubber anchor. null = follow latest; number = pinned index.
  const [scrubAnchor, setScrubAnchor] = useState<number | null>(null);

  // Hooks
  const tts = useTTS({ lang: bcp47Lang });
  const stt = useSTT({ lang: bcp47Lang });
  const canvas = useProcessCanvas(session.id);
  const bridge = useCanvasInterviewBridge(canvas.dismissedNodes);

  // Detect AI-degradation on the latest interviewer message so we can warn
  // the user that the response is a fallback (Anthropic quota / outage).
  const degradedBanner = useMemo<{ reason: string } | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'INTERVIEWER') continue;
      const meta = (m.metadata ?? null) as Record<string, unknown> | null;
      if (meta?.degraded === true) {
        const reason = typeof meta.degradedReason === 'string' ? meta.degradedReason : 'llm_unavailable';
        return { reason };
      }
      return null;
    }
    return null;
  }, [messages]);

  // Phase 6.3: derive timeline events from interviewer messages.
  // Each AI turn becomes a tick; canvas-extraction node count drives the +N badge.
  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    const out: TimelineEvent[] = [];
    for (const m of messages) {
      if (m.role !== 'INTERVIEWER') continue;
      const meta = (m.metadata ?? null) as Record<string, unknown> | null;
      const signals = meta?.signals as Record<string, unknown> | undefined;
      const extraction = signals?.canvasExtraction as
        | { nodes?: unknown[] }
        | undefined;
      const badge = extraction?.nodes?.length ?? 0;
      const text = m.content.replace(/\s+/g, ' ').slice(0, 64);
      out.push({
        id: m.id,
        label: text + (m.content.length > 64 ? '…' : ''),
        badge,
      });
    }
    return out;
  }, [messages]);

  const latestEventIdx = Math.max(0, timelineEvents.length - 1);
  const currentScrubIdx = scrubAnchor ?? latestEventIdx;
  // Track C.3 — combine the chat-scroll anchor with the canvas hook's replay
  // flag so the UI shows "Replaying" whenever either is active. (The hook
  // flag may briefly stay true mid-refresh even after the user snapped back.)
  const isReplaying = (scrubAnchor !== null && scrubAnchor < latestEventIdx) || canvas.isReplaying;

  const handleScrub = useCallback(
    (idx: number, event: TimelineEvent) => {
      const snappingToLive = idx === latestEventIdx;
      setScrubAnchor(snappingToLive ? null : idx);

      // Track C.3 — rewind the canvas itself, not just the chat scroll.
      // Sessions started before C.3 have an empty event log; the hook
      // returns early and the canvas stays at its current state.
      if (snappingToLive) {
        void canvas.replayToLatest();
      } else {
        canvas.replayToIndex(idx);
      }

      const target = document.querySelector(`[data-msg-id="${event.id}"]`);
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [latestEventIdx, canvas],
  );

  // When new messages arrive, snap the scrubber back to "Now".
  useEffect(() => {
    setScrubAnchor(null);
  }, [latestEventIdx]);

  // Map of canvas nodes for the chat panel's RefChip / contradiction bubbles.
  // Falls back to an empty object if no canvas data is loaded yet.
  const nodesById = useMemo<Record<string, ChatBubbleNode>>(() => {
    const map: Record<string, ChatBubbleNode> = {};
    for (const n of canvas.nodes) {
      if (n.type === 'sectionGroup') continue;
      map[n.id] = {
        id: n.id,
        type: n.data.canvasNodeType,
        title: n.data.label,
      };
    }
    return map;
  }, [canvas.nodes]);

  // Refs for canvas navigation/clustering controls (exposed via ProcessCanvas callbacks)
  const navRef = useRef<{
    focusOnNode: (nodeId: string) => void;
    focusOnArea: (areaId: string) => void;
    resetView: () => void;
    handleFocusRequest: (req: AgentFocusRequest) => void;
  } | null>(null);
  const clusterRef = useRef<{
    proposeClusters: (proposals: ClusterProposal[]) => void;
    getClusters: () => import('@shared/types.ts').CanvasCluster[];
  } | null>(null);

  // Stable bridge command handlers that read from refs
  const bridgeHandlers = useRef<BridgeCommandHandlers>({
    focusOnNode: (id) => navRef.current?.focusOnNode(id),
    focusOnArea: (id) => navRef.current?.focusOnArea(id),
    proposeClusters: (p) => clusterRef.current?.proposeClusters(p),
  });

  // Canvas callbacks for ProcessCanvas
  const handleNavigationReady = useCallback(
    (nav: NonNullable<typeof navRef.current>) => { navRef.current = nav; },
    [],
  );
  const handleClusteringReady = useCallback(
    (clustering: NonNullable<typeof clusterRef.current>) => { clusterRef.current = clustering; },
    [],
  );

  // Wrapped confirm/dismiss that also enqueue bridge events
  const handleConfirmNode = useCallback(
    (nodeId: string) => {
      const node = canvas.nodes.find((n) => n.id === nodeId);
      canvas.confirmNode(nodeId);
      bridge.enqueueEvent('nodeConfirmed', {
        nodeId,
        nodeLabel: node?.data.label,
        nodeType: node?.data.canvasNodeType,
      });
    },
    [canvas, bridge],
  );

  const handleDismissNode = useCallback(
    (nodeId: string) => {
      const node = canvas.nodes.find((n) => n.id === nodeId);
      bridge.enqueueEvent('nodeDismissed', {
        nodeId,
        nodeLabel: node?.data.label,
        nodeType: node?.data.canvasNodeType,
      });
      canvas.dismissNode(nodeId);
    },
    [canvas, bridge],
  );

  // Node context handler — when user adds info via canvas "+" button
  const handleNodeContextSubmit = useCallback(
    async (
      nodeId: string,
      nodeLabel: string,
      nodeType: string,
      message: string,
      kind: NodeContextKind,
    ) => {
      try {
        // Enqueue the context event for the bridge
        bridge.enqueueEvent('nodeContextAdded', { nodeId, nodeLabel, nodeType, contextText: message });

        // Build canvas snapshot from current frontend state (includes staged nodes)
        const canvasSnapshot = {
          nodes: canvas.nodes.map((n) => ({
            id: n.id,
            type: n.data.canvasNodeType,
            name: n.data.label,
            description: n.data.description,
          })),
          edges: canvas.edges.map((e) => ({
            id: e.id,
            type: (e.data?.canonicalType as string | undefined) ?? 'RELATES_TO',
            sourceId: e.source,
            targetId: e.target,
          })),
        };

        // Flush bridge events and build context
        const canvasEvents = bridge.flushEvents();
        const canvasContext = bridge.buildCanvasContext(canvas.nodes, clusterRef.current?.getClusters() ?? []);

        const response = await sendNodeContextMessage(
          session.id,
          nodeId,
          nodeLabel,
          nodeType,
          message,
          kind,
          canvasSnapshot,
          { canvasEvents, canvasContext },
        );

        // Add tagged canvas annotation to chat (grayed out, coordinator ignores it)
        setMessages((prev) => [
          ...prev,
          {
            id: `node-ctx-${Date.now()}`,
            sessionId: session.id,
            role: 'INTERVIEWEE',
            content: `[Canvas: "${nodeLabel}"] ${message}`,
            createdAt: new Date().toISOString(),
            metadata: { type: 'node-context', nodeId, nodeLabel, nodeType },
          },
        ]);

        // Apply canvas delta
        if (response.canvasDelta) {
          canvas.applyDelta(response.canvasDelta);
        }

        // Handle canvas commands from response
        bridge.handleCommands(response.canvasCommands, bridgeHandlers.current);
      } catch (error) {
        console.warn('[ChatInterview] Node context submission failed:', error);
      }
    },
    [session.id, canvas, bridge],
  );

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const startedAtRef = useRef<Date | null>(
    session.startedAt ? new Date(session.startedAt) : null
  );

  // Timer
  useEffect(() => {
    if (!startedAtRef.current) return;

    const updateTimer = () => {
      const now = Date.now();
      const start = startedAtRef.current?.getTime() ?? now;
      setElapsedSeconds(Math.max(0, Math.floor((now - start) / 1000)));
    };

    updateTimer();
    const interval = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(interval);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load chat history on mount (handles page refresh)
  useEffect(() => {
    let cancelled = false;

    async function loadHistory() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getChatHistory(session.id);
        if (cancelled) return;

        setMessages(response.messages);

        // Restore progress from the latest interviewer message metadata
        const lastInterviewerMsg = [...response.messages]
          .reverse()
          .find((m) => m.role === 'INTERVIEWER');

        if (lastInterviewerMsg?.metadata) {
          const meta = lastInterviewerMsg.metadata as Record<string, unknown>;
          if (typeof meta.section === 'string') setCurrentSection(meta.section);
          if (typeof meta.progress === 'number') setProgressPercent(meta.progress);
        }

        // Speak the greeting aloud on fresh start (only greeting message present)
        if (
          response.messages.length === 1 &&
          lastInterviewerMsg
        ) {
          tts.speak(lastInterviewerMsg.content);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load chat history';
        setError(msg);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadHistory();
    return () => {
      cancelled = true;
    };
  }, [session.id]);

  // Append STT transcript to input value
  useEffect(() => {
    if (stt.transcript) {
      setInputValue((prev) => {
        const separator = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
        return prev + separator + stt.transcript;
      });
      stt.clearTranscript();
    }
  }, [stt.transcript, stt.clearTranscript, stt]);

  // Send a message
  const handleSubmit = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isWaiting) return;

    setInputValue('');
    setError(null);
    setIsWaiting(true);

    // Stop STT if active
    if (stt.isListening) {
      stt.stopListening();
    }

    // Optimistically add the user's message
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      sessionId: session.id,
      role: 'INTERVIEWEE',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // Flush bridge events and build canvas context for the API call
      const canvasEvents = bridge.flushEvents();
      const canvasContext = bridge.buildCanvasContext(canvas.nodes, clusterRef.current?.getClusters() ?? []);
      const bridgePayload = (canvasEvents.length > 0 || canvasContext.nodeCount > 0)
        ? { canvasEvents, canvasContext }
        : undefined;

      const response = await sendChatMessage(session.id, text, bridgePayload);

      // Add the interviewer's response
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        sessionId: session.id,
        role: 'INTERVIEWER',
        content: response.message,
        metadata: response.metadata as Record<string, unknown> | undefined,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMessage]);

      // Read the response aloud
      tts.speak(response.message);

      // Update progress
      if (response.metadata) {
        if (response.metadata.section) setCurrentSection(response.metadata.section);
        if (typeof response.metadata.progress === 'number') {
          setProgressPercent(response.metadata.progress);
        }
      }

      // Apply canvas delta if present (FEAT-11)
      if (response.canvasDelta) {
        canvas.applyDelta(response.canvasDelta);
      }

      // Handle canvas commands from agent (focus, highlight, clustering)
      bridge.handleCommands(response.canvasCommands, bridgeHandlers.current);

      // Handle completion
      if (response.metadata?.isComplete) {
        onComplete?.();
        // Trigger knowledge extraction in a separate request
        triggerExtraction(session.id).catch(() => {});
        // Small delay so the user can see the final message
        setTimeout(() => {
          navigate(`/complete/${session.id}`);
        }, 2000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send message';
      setError(msg);
      // Remove the optimistic user message on error
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      setInputValue(text); // Restore the input
    } finally {
      setIsWaiting(false);
    }
  }, [inputValue, isWaiting, session.id, tts, stt, onComplete, navigate, canvas, bridge]);

  // End interview manually
  const handleEndInterview = useCallback(async () => {
    if (isEnding) return;

    setIsEnding(true);
    setError(null);

    try {
      await endChatInterview(session.id);
      onComplete?.();
      // Trigger knowledge extraction in a separate request
      triggerExtraction(session.id).catch(() => {});
      navigate(`/complete/${session.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to end interview';
      setError(msg);
    } finally {
      setIsEnding(false);
    }
  }, [session.id, isEnding, onComplete, navigate]);

  // Toggle STT
  const handleToggleSTT = useCallback(() => {
    if (stt.isListening) {
      stt.stopListening();
    } else {
      stt.startListening();
    }
  }, [stt]);

  // Loading state
  if (isLoading) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col px-4 py-8">
        <div className="space-y-3 rounded-2xl border border-border bg-card p-8 shadow-lg">
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="card" />
        </div>
      </section>
    );
  }

  // ── Chat Panel (reused across all breakpoints) ───────────────────────────

  const chatPanel = (
    <div className="flex h-full flex-col">
      {/* Phase 3.1 chat header — Kaiva persona, session meta, progress.
          v1.7: paper surface; tonal shift to off-white-container messages
          area below replaces the old 1px divider. */}
      <div
        className="flex-shrink-0 px-[18px] pb-[10px] pt-[14px]"
        style={{ background: 'hsl(var(--card))' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <KaivaAvatar size={22} pulse />
            <KaivaWordmark size={15} tone="ink" />
            <span
              className="text-[11px] tracking-[0.02em]"
              style={{ color: 'hsl(var(--muted-foreground))' }}
            >
              — your interviewer
            </span>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {tts.isSupported && (
              <button
                type="button"
                onClick={tts.toggleMute}
                className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors hover:bg-muted"
                style={{
                  color: tts.isMuted
                    ? 'hsl(var(--muted-foreground))'
                    : 'hsl(var(--sienna))',
                }}
                aria-label={tts.isMuted ? 'Unmute voice readout' : 'Mute voice readout'}
                title={tts.isMuted ? 'Unmute voice readout' : 'Mute voice readout'}
              >
                {tts.isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </button>
            )}
            <span
              className="inline-flex items-center rounded-full px-1.5 py-[2px] kv-eyebrow text-[10px] font-semibold"
              style={{
                background: 'hsl(var(--teal))',
                color: 'hsl(var(--slate-navy))',
              }}
            >
              live
            </span>
          </div>
        </div>

        <div
          className="mt-1 truncate text-[11px]"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          {[session.intervieweeEmail, session.language, session.coordinatorModel]
            .filter(Boolean)
            .join(' · ')}
        </div>

        <ChatProgress
          currentSection={currentSection}
          progressPercent={progressPercent}
        />
      </div>

      {/* Degraded-mode warning — visible whenever the latest AI message was a
          fallback because the LLM call failed (quota / rate limit / outage). */}
      {degradedBanner && (
        <div
          className="flex-shrink-0 px-4 py-2"
          style={{ background: 'hsl(var(--off-white-container))' }}
          role="status"
          aria-live="polite"
        >
          <div className="rounded-lg border border-kaiva-warning/30 bg-kaiva-warning/10 px-3 py-2 text-xs text-kaiva-warning">
            {degradedBanner.reason === 'llm_quota_exhausted'
              ? 'KI-Antworten gerade eingeschraenkt: API-Kontingent ausgeschoepft. Bitte spaeter erneut versuchen.'
              : degradedBanner.reason === 'llm_rate_limited'
                ? 'KI-Antworten gerade eingeschraenkt: Anbieter limitiert kurzzeitig. Bitte spaeter erneut versuchen.'
                : 'KI-Antworten gerade eingeschraenkt: Modell vorübergehend nicht erreichbar. Bitte spaeter erneut versuchen.'}
          </div>
        </div>
      )}

      {/* Messages area — sits on off-white-container so it tonally separates
          from the paper header above and paper composer below. The result
          reads as a layered card: persona strip / conversation thread /
          composer strip — each visually distinct without using 1px dividers. */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ background: 'hsl(var(--off-white-container))' }}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Interview transcript"
      >
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} data-msg-id={msg.id}>
              <ChatMessageBubble
                msg={chatMessageToBubble(msg)}
                nodesById={nodesById}
              />
            </div>
          ))}

          {/* Live thinking bubble — shown only while waiting for the AI response.
              Synthetic message: not persisted, not in `messages`. */}
          {isWaiting && (
            <ChatMessageBubble
              msg={{
                kind: 'thinking',
                id: 'thinking-pending',
                thoughts: [{ text: 'Analysing your response…', icon: 'Sparkles' }],
              }}
              nodesById={nodesById}
            />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="flex-shrink-0 bg-card px-4 py-2">
          <ErrorPill message={error} onRetry={() => setError(null)} />
        </div>
      )}

      {/* STT error */}
      {stt.error && (
        <div className="flex-shrink-0 bg-card px-4 py-2">
          <div className="rounded-lg border border-kaiva-warning/30 bg-kaiva-warning/10 px-3 py-2 text-xs text-kaiva-warning">
            {stt.error}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0">
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSubmit={() => void handleSubmit()}
          isWaiting={isWaiting}
          isListening={stt.isListening}
          isSTTSupported={stt.isSupported}
          interimTranscript={stt.interimTranscript}
          onToggleSTT={handleToggleSTT}
          placeholder={
            session.language === 'de'
              ? 'Schreiben Sie Ihre Antwort...'
              : 'Type your response...'
          }
        />
      </div>

      {/* Browser TTS/STT unavailable notices */}
      {!tts.isSupported && (
        <p className="flex-shrink-0 py-1 text-center text-[10px] text-muted-foreground">
          Voice readout is unavailable in this browser.
        </p>
      )}
    </div>
  );

  // ── Two-Panel Layout ─────────────────────────────────────────────────────

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      {/* Sprint 10.3.B — keyboard skip link. Hidden until focused via Tab,
          jumps focus past the topbar straight to the canvas region. */}
      <a
        href="#canvas-region"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:px-3 focus:py-1.5 focus:text-xs focus:font-medium focus:no-underline focus:outline-none"
        style={{
          background: 'hsl(var(--sienna))',
          color: 'hsl(var(--cream))',
        }}
      >
        Skip to canvas
      </a>

      <SessionTopbar
        sessionName={session.sessionName}
        elapsedSeconds={elapsedSeconds}
        focusMode={focusMode}
        onFocusModeToggle={() => setFocusMode((v) => !v)}
        showLabels={showLabels}
        onLabelsToggle={() => setShowLabels((v) => !v)}
        stagedCount={canvas.nodes.filter((n) => n.data.staged).length}
        onAcceptAll={canvas.acceptAllStaged}
        onSkipAll={canvas.skipAllStaged}
        onFinish={() => void handleEndInterview()}
        isFinishing={isEnding}
      />
    <section className="flex w-full min-h-0 flex-1 gap-0 px-2 py-2 lg:px-4">
      {/* Canvas Panel — visible on desktop, toggle on tablet, hidden on mobile.
          v1.7: workspace surface, off-white background continuous with the page.
          Substrate dot-grid is painted internally by ProcessCanvas via React Flow.
          No shadow (per v1.7 cards-don't-cast-shadows rule); hairline-2 border
          gives it a defined edge against the page substrate. */}
      <div
        className="hidden h-full overflow-hidden rounded-l-2xl border border-r-0 lg:block lg:w-[70%]"
        style={{
          background: 'hsl(var(--background))',
          borderColor: 'hsl(var(--hairline-2))',
        }}
      >
        <ProcessCanvas
          sessionId={session.id}
          canvas={canvas}
          onNodeContextSubmit={handleNodeContextSubmit}
          onNavigationReady={handleNavigationReady}
          onClusteringReady={handleClusteringReady}
          highlightedNodeIds={bridge.highlightedNodeIds}
          onConfirmNode={handleConfirmNode}
          onDismissNode={handleDismissNode}
          showLabels={showLabels}
          focusMode={focusMode}
        />
      </div>

      {/* Tablet: canvas overlay when toggled (md to lg) */}
      {showCanvas && (
        <div className="fixed inset-0 z-40 hidden md:block lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowCanvas(false)}
            aria-hidden="true"
          />
          {/* Canvas panel */}
          <div className="absolute bottom-4 left-4 right-4 top-4 overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <h2 className="text-sm font-semibold text-foreground">
                Process Canvas
              </h2>
              <button
                type="button"
                onClick={() => setShowCanvas(false)}
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted"
                aria-label="Close canvas"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-[calc(100%-44px)]">
              <ProcessCanvas
                canvas={canvas}
                onNodeContextSubmit={handleNodeContextSubmit}
                onNavigationReady={handleNavigationReady}
                onClusteringReady={handleClusteringReady}
                highlightedNodeIds={bridge.highlightedNodeIds}
                onConfirmNode={handleConfirmNode}
                onDismissNode={handleDismissNode}
                showLabels={showLabels}
                focusMode={focusMode}
              />
            </div>
          </div>
        </div>
      )}

      {/* Chat Panel — v1.7 paper "writing surface" docked to the workspace.
          Visible as a distinct surface against the off-white canvas via:
          (a) Paper #FFFFFF background vs. canvas off-white #F8F7F4,
          (b) full hairline-2 border including a restored left edge that
          gives the seam a clear vertical stroke, (c) inner tonal layering
          (paper header / off-white-container messages / paper composer). */}
      <div
        className={cn(
          'h-full overflow-hidden border',
          // Desktop: right panel, 30% width — left border restored for clear seam
          'lg:w-[30%] lg:rounded-r-2xl',
          // Tablet & mobile: full width, full rounded
          'w-full rounded-2xl lg:rounded-l-none',
        )}
        style={{
          background: 'hsl(var(--card))',
          borderColor: 'hsl(var(--hairline-2))',
        }}
      >
        {chatPanel}
      </div>

    </section>

    {/* Phase 6.3 read-only timeline scrubber — rewinds chat scroll only */}
    <TimelineScrubber
      events={timelineEvents}
      currentIdx={currentScrubIdx}
      isReplaying={isReplaying}
      onScrub={handleScrub}
    />

    {/* Developer Insights — dev-only (Phase 0 decision: gate behind import.meta.env.DEV) */}
    {import.meta.env.DEV && (
      <DevInsightsModal
        sessionId={session.id}
        messages={messages}
        canvasNodeCount={canvas.nodes.filter((n) => n.type !== 'sectionGroup').length}
        canvasStagedCount={canvas.nodes.filter((n) => n.data.staged).length}
        dismissedCount={canvas.dismissedNodes.length}
        clusterCount={clusterRef.current?.getClusters().length ?? 0}
      />
    )}
    </div>
  );
}
